/**
 * Client API centralisé.
 *
 * Toute réponse non-ok devient une ApiError porteuse d'un message affichable :
 * sans ça, un 401/403/500 renvoie `{detail: ...}` que les écrans consommaient
 * comme une réponse valide (liste `undefined` → écran blanc), et un 422 renvoie
 * un tableau que React refuse de rendre.
 */

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// FastAPI : `detail` est une chaîne (HTTPException) ou une liste (validation 422)
function extractDetail(data, fallback) {
  const detail = data && data.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (first && typeof first.msg === "string") return first.msg;
    return "Vérifiez les informations saisies";
  }
  return fallback;
}

// L'application enregistre ici de quoi ramener l'utilisateur à la connexion
let unauthorizedHandler = null;

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

export async function apiFetch(url, options = {}, fallback = "Une erreur est survenue") {
  let res;
  try {
    res = await fetch(url, options);
  } catch {
    throw new ApiError("Impossible de contacter le serveur", 0);
  }

  let data = null;
  if (res.status !== 204) {
    data = await res.json().catch(() => null);
  }

  if (!res.ok) {
    // Session expirée ou révoquée : l'app repasse par l'écran de connexion
    if (res.status === 401 && unauthorizedHandler) unauthorizedHandler();
    throw new ApiError(extractDetail(data, fallback), res.status);
  }
  return data;
}

export function apiGet(url, fallback) {
  return apiFetch(url, {}, fallback);
}

export function apiSend(url, method, body, fallback) {
  return apiFetch(
    url,
    {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    fallback
  );
}

/** Télécharge un fichier binaire (PDF…) et déclenche l'enregistrement côté
 * navigateur, avec la même gestion d'erreurs que les appels JSON. */
export async function apiDownload(url, options = {}, filename, fallback) {
  let res;
  try {
    res = await fetch(url, options);
  } catch {
    throw new ApiError("Impossible de contacter le serveur", 0);
  }
  if (!res.ok) {
    if (res.status === 401 && unauthorizedHandler) unauthorizedHandler();
    const data = await res.json().catch(() => null);
    throw new ApiError(extractDetail(data, fallback), res.status);
  }
  const blobUrl = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

/** Message affichable pour n'importe quelle exception remontée d'un appel API. */
export function errorMessage(e, fallback = "Une erreur est survenue") {
  return e instanceof ApiError ? e.message : fallback;
}
